import { snapToPixel } from '../util/math.js';
import { fieldMetrics, screenXForYard } from '../field/metrics.js';
import { clonePlayers, carriedBallPoint } from '../player/model.js';
import type { ArcadeReplayFrameSnapshot } from '../../arcade/contracts.js';
import { arcadeDivingCatchVisualLiftYards } from '../../arcade/diving-catch.js';

const ARCADE_FIELD_WIDTH = 53.33;
const TOUCHDOWN_PRESENTATION_RUNOUT_FRAMES = 48;
const TOUCHDOWN_PRESENTATION_MIN_CARRIER_SPEED = 0.06;
const TOUCHDOWN_PRESENTATION_MAX_CARRIER_SPEED = 0.12;

/**
 * Continue the visible motion briefly after an authoritative touchdown.
 *
 * Gameplay physics intentionally stops the instant possession breaks the
 * plane so a later collision cannot turn a score into a tackle or fumble. The
 * renderer still needs a safe, collision-free scorer walk-in. Everyone except
 * the ball carrier stays on the authoritative scoring pose so the dead play
 * cannot turn into a chaotic full-team runout.
 */
export function extendTouchdownPresentationFrames(
  frames: ArcadeReplayFrameSnapshot[],
  touchdown: boolean,
): ArcadeReplayFrameSnapshot[] {
  if (!touchdown || frames.length === 0) {
    return frames;
  }
  const last = frames.at(-1)!;
  if (last.ball.state !== 'carried' || !last.ball.ownerId) {
    return frames;
  }

  const extended = [...frames];
  let previous = structuredClone(last);
  for (let offset = 1; offset <= TOUCHDOWN_PRESENTATION_RUNOUT_FRAMES; offset += 1) {
    const players = previous.players.map((player) => {
      const isCarrier = player.entityId === previous.ball.ownerId;
      if (!isCarrier) {
        return { ...player, vx: 0, vy: 0 };
      }
      const scoringDirection = player.side === 'defense' ? -1 : 1;
      const vx = scoringDirection * Math.max(
        TOUCHDOWN_PRESENTATION_MIN_CARRIER_SPEED,
        Math.min(TOUCHDOWN_PRESENTATION_MAX_CARRIER_SPEED, Math.abs(player.vx)),
      );
      return {
        ...player,
        x: Math.max(6.5, Math.min(113.5, player.x + vx)),
        y: Math.max(0.5, Math.min(ARCADE_FIELD_WIDTH - 0.5, player.y)),
        vx,
        vy: 0,
        state: 'touchdown-runout',
        assignmentKind: 'touchdown-runout',
        engagementState: null,
        beingTackledBy: [],
        tackleProgress: 0,
      };
    });
    const owner = players.find((player) => player.entityId === previous.ball.ownerId);
    const next: ArcadeReplayFrameSnapshot = {
      ...previous,
      simFrame: last.simFrame + offset,
      time: last.time + offset / 60,
      playState: 'touchdown-runout',
      players,
      ball: owner
        ? { ...previous.ball, x: owner.x, y: owner.y, vx: owner.vx, vy: owner.vy }
        : previous.ball,
      collisions: [],
      contacts: [],
    };
    extended.push(next);
    previous = next;
  }
  return extended;
}

function isArcadeTrenchRole(role: string): boolean {
  return role === 'LT'
    || role === 'LG'
    || role === 'C'
    || role === 'RG'
    || role === 'RT'
    || role === 'DE1'
    || role === 'DT1'
    || role === 'DT2'
    || role === 'DE2';
}

function suppressArcadeReplayMotion(source: any, role: string): boolean {
  const assignment = String(source?.assignmentKind || '');
  const engagement = String(source?.engagementState || '');
  if (assignment.startsWith('incomplete-')) {
    return true;
  }
  if (
    assignment === 'catch-secure-dive'
    || assignment === 'catch-dive-down'
    || assignment === 'catch-dive-rise'
  ) {
    return true;
  }
  if ((engagement === 'engaging' || engagement === 'screened') && isArcadeTrenchRole(role)) {
    return true;
  }
  return false;
}

export function mapArcadeFieldXToWorldX(fieldX: number, direction: number): number {
  return screenXForYard(fieldX - 10, direction);
}

export function mapArcadeFieldYToWorldY(
  fieldY: number,
  metrics: ReturnType<typeof fieldMetrics>,
): number {
  return snapToPixel(metrics.fieldTop + (fieldY / ARCADE_FIELD_WIDTH) * metrics.fieldHeight);
}

export function applyArcadeReplayFrameToPlayers(
  players: any[],
  replayFrame: ArcadeReplayFrameSnapshot,
  direction: number,
  metrics: ReturnType<typeof fieldMetrics>,
  options: {
    movingThreshold?: number;
    previousReplayFrame?: ArcadeReplayFrameSnapshot | null;
  } = {},
): any[] {
  const movingThreshold = options.movingThreshold ?? 0.06;
  const mapped = clonePlayers(players);
  const byRole = new Map(
    replayFrame.players
      .filter((player) => player.rendererRole)
      .map((player) => [player.rendererRole, player]),
  );
  const previousByRole = new Map(
    (options.previousReplayFrame?.players || [])
      .filter((player) => player.rendererRole)
      .map((player) => [player.rendererRole, player]),
  );

  return mapped.map((player) => {
    const source = byRole.get(player.role);
    if (!source) {
      return { ...player, moving: false };
    }

    const divingCatchAssignment = String(source.assignmentKind || '');
    const divingCatch = divingCatchAssignment === 'catch-secure-dive'
      || divingCatchAssignment === 'catch-dive-extension'
      || divingCatchAssignment === 'catch-dive-contact'
      || divingCatchAssignment === 'catch-dive-landing'
      || divingCatchAssignment === 'catch-dive-down';
    const sackFinishFall = player.role === 'QB'
      && source.assignmentKind === 'sack-finish'
      && source.tackleProgress >= 0.72;
    const sackTackler = source.assignmentKind === 'sack-engage'
      || source.assignmentKind === 'sack-wrap'
      || source.assignmentKind === 'sack-finish-stand';
    const divingCatchRise = source.assignmentKind === 'catch-dive-rise';
    const arcadeVerticalLift = (source.visualLiftYards
      ?? arcadeDivingCatchVisualLiftYards(source.assignmentKind))
      * (metrics.fieldHeight / ARCADE_FIELD_WIDTH);
    const previousSource = previousByRole.get(player.role);
    return {
      ...player,
      x: mapArcadeFieldXToWorldX(source.x, direction),
      y: mapArcadeFieldYToWorldY(source.y, metrics) - arcadeVerticalLift,
      moving: !suppressArcadeReplayMotion(source, player.role)
        && Math.hypot(source.vx, source.vy) > movingThreshold
        && source.engagementState !== 'tackled',
      down: source.engagementState === 'tackled' || divingCatch || sackFinishFall,
      tackled: source.engagementState === 'tackled' || divingCatch || sackFinishFall,
      moveStyle: sackTackler || divingCatchRise ? 'engaged' : player.moveStyle,
      engagementState: source.engagementState,
      tackleProgress: source.tackleProgress,
      arcadeState: source.state,
      arcadeAssignment: source.assignmentKind,
      arcadeVerticalLift,
      arcadeVisualVx: Number(source.vx || 0) * direction,
      arcadeVisualVy: Number(source.vy || 0),
      arcadePreviousVisualVx: Number(previousSource?.vx ?? source.vx ?? 0) * direction,
      arcadePreviousVisualVy: Number(previousSource?.vy ?? source.vy ?? 0),
    };
  });
}

export function blendArcadeReplayPlayersFromShell(
  shellPlayers: any[],
  replayPlayers: any[],
  replayFrame: ArcadeReplayFrameSnapshot,
  options: { blendFrames?: number; revealMotionAt?: number; holdFrames?: number } = {},
): any[] {
  const blendFrames = Math.max(1, options.blendFrames ?? 12);
  const holdFrames = Math.max(0, Math.min(blendFrames - 1, options.holdFrames ?? 2));
  const revealMotionAt = options.revealMotionAt ?? 0.45;
  const linearBlendAmount = Math.max(0, Math.min(1, (replayFrame.simFrame - holdFrames) / Math.max(1, blendFrames - holdFrames)));
  const blendAmount = linearBlendAmount * linearBlendAmount * (3 - 2 * linearBlendAmount);
  if (blendAmount >= 1) {
    return replayPlayers;
  }

  return replayPlayers.map((player) => {
    const shell = shellPlayers.find((candidate) => candidate.role === player.role);
    if (!shell) {
      return player;
    }
    return {
      ...player,
      x: snapToPixel(shell.x + (player.x - shell.x) * blendAmount),
      y: snapToPixel(shell.y + (player.y - shell.y) * blendAmount),
      moving: blendAmount > revealMotionAt ? player.moving : false,
    };
  });
}

export function defaultArcadeOwnerBallPointForReplayFrame(
  replayFrame: ArcadeReplayFrameSnapshot,
  direction: number,
  metrics: ReturnType<typeof fieldMetrics>,
  renderedPlayers: any[] = [],
  options: { handYOffset?: number } = {},
) {
  if (replayFrame.ball.state !== 'carried' || !replayFrame.ball.ownerRendererRole) {
    return null;
  }

  const owner = replayFrame.players.find((player) => player.rendererRole === replayFrame.ball.ownerRendererRole);
  const renderedOwner = renderedPlayers.find((player) => player.role === replayFrame.ball.ownerRendererRole);
  if (!owner) {
    return null;
  }
  const mappedOwner = renderedOwner || {
    ...owner,
    role: owner.rendererRole,
    x: mapArcadeFieldXToWorldX(owner.x, direction),
    y: mapArcadeFieldYToWorldY(owner.y, metrics),
    moving: Math.hypot(owner.vx, owner.vy) > 0.06,
  };
  const ownerTeamType = owner.side === 'defense' ? 'defense' : 'offense';
  const carryPoint = carriedBallPoint(
    mappedOwner,
    mapArcadeFieldXToWorldX(owner.x, direction),
    snapToPixel(mapArcadeFieldYToWorldY(owner.y, metrics) + (options.handYOffset ?? 8)),
    owner.side === 'offense' ? direction : -direction,
    ownerTeamType,
  );

  return {
    ballX: carryPoint.ballX,
    ballY: carryPoint.ballY,
    arcadeState: replayFrame.ball.state,
    arcadeOwnerRole: replayFrame.ball.ownerRendererRole,
  };
}
